import { useQuery } from "@tanstack/react-query";

import React, { useContext, useEffect, useState } from "react";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFooter,
  GridFooterContainer,
  useGridApiContext,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { ArchivedSensor } from "@safetyculture/s12-apis-connect-web/s12/sensors/admin/v1/sensors_pb";
import { SensorType } from "@safetyculture/s12-apis-connect-web/s12/sensors/admin/v1/sensors_pb";

import { convertTimestampToDate } from "../../../../helpers/time";
import { archivedSensorsOptions } from "../../../../queries/archived-sensors";
import { UrlParamTypes } from "../../../../types/org-display";
import { SensorTypeChip } from "components/sensors/table/chips";
import { ColumnHeader } from "components/sensors/table/column-header";
import CopyableCell from "components/sensors/table/copyable-cell";
import {
  DATE_FORMAT,
  copyableDateCellProps,
  localTimezoneLabel,
} from "components/sensors/table/copyable-date";
import { MonoText } from "components/sensors/table/mono-text";
import { NoResultsOverlay } from "components/sensors/table/no-results-overlay";
import { noValueText } from "components/sensors/table/no-value-text";
import {
  COLUMN_WIDTH,
  CellRow,
  TABLE_HEIGHT,
  dataGridSx,
} from "components/sensors/table/table-layout";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

const sensorTypeFilterOptions: Array<{ value: SensorType; label: string }> = [
  { value: SensorType.UNSPECIFIED, label: "All" },
  { value: SensorType.HARDWARE, label: "Hardware" },
  { value: SensorType.WEATHER, label: "Weather" },
];

const displayID = (row: ArchivedSensor): string =>
  row.lastDevice?.displayId ?? "";

const sensorTypeToText = (sensorType: SensorType): string => {
  switch (sensorType) {
    case SensorType.HARDWARE:
      return "Sensor";
    case SensorType.WEATHER:
      return "Weather";
    default:
      return "";
  }
};

// Downloads the CSV directly on click, skipping the extra menu step the
// default GridToolbarExport shows for a single format. This is the only
// table paginated server-side, so the export only covers the loaded page,
// unlike Sensors/Gateways -- worth calling out via tooltip.
function ExportFooter() {
  const apiRef = useGridApiContext();
  return (
    <GridFooterContainer>
      <Tooltip title="Exports only the current page">
        <Button
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => apiRef.current.exportDataAsCsv()}
          sx={{ marginLeft: 1 }}
        >
          Export CSV
        </Button>
      </Tooltip>
      <GridFooter sx={{ border: "none" }} />
    </GridFooterContainer>
  );
}

const OrganisationArchivedSensorsTable = () => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { showError } = useContext(SnackbarContext);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sensorType, setSensorType] = useState<SensorType>(
    SensorType.UNSPECIFIED,
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(timeout);
  }, [query]);

  // A filter change while on a later page would otherwise request a page that no
  // longer exists for the new, narrower result set, so reset back to page 0.
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, sensorType]);

  const { data, isLoading, error } = useQuery(
    archivedSensorsOptions({
      regionCode,
      roleId,
      page,
      pageSize,
      query: debouncedQuery,
      sensorType,
    }),
  );
  const sensorsList: ArchivedSensor[] = data?.sensors ?? [];
  const rowCount = data?.page?.total ?? 0;

  useEffect(() => {
    if (error) {
      showError(error as GrpcError);
    }
  }, [error, showError]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      // Wider than the other tabs' shared COLUMN_WIDTH.name -- this
      // table has fewer columns overall, so there's more room to spare.
      width: 220,
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.name} tabIndex={tabIndex} />;
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: 240,
      valueGetter: ({ row }) => row.location?.name ?? "",
      renderCell({ row, tabIndex }) {
        return (
          <CopyableCell value={row.location?.name ?? ""} tabIndex={tabIndex} />
        );
      },
    },
    {
      field: "sensorType",
      headerName: "Type",
      width: 110,
      align: "left",
      renderHeader: () => <ColumnHeader label="Type" tooltip="Sensor type" />,
      valueGetter: ({ row }) => sensorTypeToText(row.sensorType),
      renderCell({ value }) {
        return (
          <CellRow>
            <SensorTypeChip sensorType={value} />
          </CellRow>
        );
      },
    },
    {
      field: "createdAt",
      headerName: `Created at (${localTimezoneLabel()})`,
      width: 180,
      align: "left",
      renderHeader: () => (
        <ColumnHeader
          label="Created at"
          tooltip={`Times shown in ${localTimezoneLabel()}`}
        />
      ),
      valueGetter({ row }) {
        return row.createdAt
          ? DateTime.fromJSDate(convertTimestampToDate(row.createdAt)).toFormat(
              DATE_FORMAT,
            )
          : "";
      },
      renderCell({ row, tabIndex }) {
        const createdAt = row.createdAt
          ? DateTime.fromJSDate(convertTimestampToDate(row.createdAt))
          : null;
        return (
          <CellRow>
            <CopyableCell
              {...copyableDateCellProps(createdAt, "Unknown")}
              tabIndex={tabIndex}
            />
          </CellRow>
        );
      },
    },
    {
      field: "archivedAt",
      headerName: `Archived at (${localTimezoneLabel()})`,
      width: 180,
      align: "left",
      renderHeader: () => (
        <ColumnHeader
          label="Archived at"
          tooltip={`Times shown in ${localTimezoneLabel()}`}
        />
      ),
      valueGetter({ row }) {
        return row.archivedAt
          ? DateTime.fromJSDate(
              convertTimestampToDate(row.archivedAt),
            ).toFormat(DATE_FORMAT)
          : "";
      },
      renderCell({ row, tabIndex }) {
        const archivedAt = row.archivedAt
          ? DateTime.fromJSDate(convertTimestampToDate(row.archivedAt))
          : null;
        return (
          <CellRow>
            <CopyableCell
              {...copyableDateCellProps(archivedAt, "Unknown")}
              tabIndex={tabIndex}
            />
          </CellRow>
        );
      },
    },
    {
      field: "source",
      headerName: "Source name",
      width: COLUMN_WIDTH.sourceName,
      align: "left",
      valueGetter: ({ row }) => row.lastDevice?.sourceName ?? "",
      renderCell({ row, tabIndex }) {
        return (
          <CopyableCell
            value={row.lastDevice?.sourceName ?? ""}
            tabIndex={tabIndex}
          >
            <MonoText>{row.lastDevice?.sourceName ?? ""}</MonoText>
          </CopyableCell>
        );
      },
    },
    {
      field: "sourceId",
      headerName: "Source ID",
      // Wider than the other tabs' shared COLUMN_WIDTH.id -- this table has
      // fewer columns overall, so there's more room to spare.
      width: 380,
      align: "left",
      valueGetter: ({ row }) => row.lastDevice?.sourceId ?? "",
      renderCell: ({ row, tabIndex }) => (
        <CopyableCell
          value={row.lastDevice?.sourceId ?? ""}
          tabIndex={tabIndex}
        >
          <MonoText>{row.lastDevice?.sourceId ?? ""}</MonoText>
        </CopyableCell>
      ),
    },
    {
      field: "displayId",
      headerName: "Display ID",
      width: COLUMN_WIDTH.id,
      renderHeader: () => (
        <ColumnHeader
          label="Display ID"
          tooltip="Blank means the same as Source ID"
        />
      ),
      valueGetter: ({ row }) => displayID(row),
      renderCell({ row, tabIndex }) {
        const id = displayID(row);
        return (
          <CopyableCell value={id} tabIndex={tabIndex}>
            {id ? <MonoText>{id}</MonoText> : noValueText("-")}
          </CopyableCell>
        );
      },
    },
    {
      field: "sensorId",
      headerName: "Internal sensor ID",
      width: COLUMN_WIDTH.id,
      align: "left",
      renderCell({ row, tabIndex }) {
        return <CopyableCell value={row.sensorId} tabIndex={tabIndex} />;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <TextField
          label="Search"
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or location"
          sx={{ width: 280 }}
        />
        <TextField
          select
          label="Type"
          size="small"
          value={sensorType}
          onChange={(e) => setSensorType(Number(e.target.value) as SensorType)}
          sx={{ width: 160 }}
        >
          {sensorTypeFilterOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ height: TABLE_HEIGHT, width: "100%" }}>
        <DataGrid
          rows={sensorsList}
          columns={columns}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.sensorId}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: isLoading ? LinearProgress : NoResultsOverlay,
            Footer: ExportFooter,
          }}
          data-testid="org-archived-sensor-table"
          disableVirtualization
          loading={isLoading}
          sortingOrder={["asc", "desc"]}
          initialState={{
            columns: {
              columnVisibilityModel: {
                sensorId: false,
              },
            },
          }}
          sx={dataGridSx}
        />
      </Box>
    </>
  );
};

export default OrganisationArchivedSensorsTable;
